import {
  Button,
  Chip,
  Code,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Switch,
  Tab,
  Tabs,
  Textarea,
  Tooltip,
} from "@nextui-org/react";
import React, { useEffect, useState } from "react";
import { colorPicker } from "../../controllers/basicControllers";
import { postData } from "../../controllers/vectordbController";
import { Info } from "lucide-react";
import useGetOrgDetail from "../../hooks/useGetOrgDetail";
import useGetUserDetail from "../../hooks/useGetUserInfo";
import { getOnedata } from "../../controllers/APIController";

/**
 * AddModal Component
 * Modal for adding new anomalies to the system
 * Handles anomaly creation with metadata, tags, and visibility settings
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.setIsOpen - Function to control modal open state
 * @param {Object} props.threadResp - Thread response data containing anomaly information
 * @returns {JSX.Element} Anomaly creation modal
 */
const AddModal = ({ isOpen, setIsOpen, threadResp }) => {
  // Component state
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const [dataProduct, setDataProduct] = useState(null);
  const [isPublic, setIsPublic] = useState(true);

  // Custom hooks for organization and user details
  const { OrgDetail: spaceInfo } = useGetOrgDetail();
  const { userDetails: userInfo } = useGetUserDetail();

  /**
   * Handle tag input key events
   * Adds new tags on Enter key press
   * @param {KeyboardEvent} event - Keyboard event object
   */
  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      const trimmedTag = tagInput.trim();
      
      if (tags.includes(trimmedTag) || trimmedTag === "") {
        return;
      } else {
        setTags((prev) => [...prev, trimmedTag]);
        setTagInput("");
      }
    }
  };

  /**
   * Submit anomaly data to the system
   * Validates form and sends data to vector database
   */
  const handleSubmit = async () => {
    if (name.trim() === "" || description.trim() === "") {
      return;
    }
    
    try {
      const formData = {
        name: name.trim(),
        description: description.trim(),
        sql: [threadResp?.chart?.sql_statement],
        tags: tags,
        dataproduct_id: String(threadResp?.data_product),
        type: threadResp?.anomaly,
        userId: userInfo.id,
        public: isPublic,
      };
      
      const id = Date.now();
      await postData(
        id,
        formData,
        spaceInfo[0]?.attributes?.SpaceName,
        "anomaly"
      );
      setIsOpen(false);
    } catch (error) {
      console.error("Error adding anomaly:", error);
    }
  };

  /**
   * Fetch data product information on component mount
   * Sets public visibility based on data product settings
   */
  useEffect(() => {
    const fetchDataProduct = async () => {
      try {
        const response = await getOnedata(
          "collections",
          Number(threadResp?.data_product)
        );
        setDataProduct(response.data);
        
        if (response.data.attributes.Public === false) {
          setIsPublic(false);
        }
      } catch (error) {
        console.error("Error fetching data product:", error);
      }
    };
    
    if (threadResp?.data_product) {
      fetchDataProduct();
    }
  }, [threadResp]);

  /**
   * Reset form state when modal opens/closes
   */
  useEffect(() => {
    setName("");
    setDescription("");
    setTags([]);
    setTagInput("");
  }, [isOpen]);

  return (
    <Modal isOpen={isOpen} onClose={() => setIsOpen(false)}>
      <ModalContent>
        {/* Modal Header */}
        <ModalHeader>Add Anomaly</ModalHeader>
        
        {/* Modal Body */}
        <ModalBody className="flex flex-col gap-4">
          {/* Anomaly Name Input */}
          <Input
            placeholder="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            label="Name"
            labelPlacement="outside"
          />
          
          {/* Anomaly Description Input */}
          <Textarea
            placeholder="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            label="Description"
            labelPlacement="outside"
          />
          
          {/* Public Visibility Toggle */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1">
              <div>Public</div>
              <Tooltip content="Allowed only if Data Product is public">
                <Info height={16} />
              </Tooltip>
            </div>
            <Switch
              isDisabled={!dataProduct?.attributes?.Public}
              isSelected={isPublic}
              onValueChange={setIsPublic}
            />
          </div>
          
          {/* Tags Management */}
          <div className="flex flex-col gap-2">
            <div>Tags</div>
            <div className="bg-default-100 py-2 rounded-lg">
              {/* Existing Tags Display */}
              <div className="flex flex-wrap gap-2 px-2">
                {tags.map((tag, index) => (
                  <Chip
                    onClose={() =>
                      setTags((prev) => prev.filter((item) => tag !== item))
                    }
                    variant="flat"
                    key={`tag-${index}`}
                    size="sm"
                    color={colorPicker(tag.length)}
                  >
                    {tag}
                  </Chip>
                ))}
              </div>
              
              {/* Tag Input */}
              <Textarea
                classNames={{
                  inputWrapper: "data-[hover=true]:bg-default-100 shadow-none",
                }}
                placeholder="Type then press enter"
                onKeyDown={handleKeyDown}
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
              />
            </div>
          </div>
        </ModalBody>
        
        {/* Modal Footer */}
        <ModalFooter className="flex justify-end">
          <Button
            onClick={handleSubmit}
            size="sm"
            color="primary"
            disabled={name.trim() === "" || description.trim() === ""}
            className="w-20"
          >
            Add
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

/**
 * AnomalyDrawer Component
 * Main anomaly management drawer with creation and visualization
 * Provides interface for adding anomalies and viewing analysis
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the drawer is open
 * @param {Function} props.setIsOpen - Function to control drawer open state
 * @param {Object} props.threadResp - Thread response data containing anomaly information
 * @returns {JSX.Element} Anomaly management drawer
 */
const AnomalyDrawer = ({ isOpen, setIsOpen, threadResp }) => {
  // Component state
  const [selectedTab, setSelectedTab] = useState("contri");
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  return (
    <Modal
      radius="none"
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      className="absolute !m-0 top-0 right-0 h-screen z-100 overflow-y-auto"
      hideCloseButton={true}
      size="4xl"
    >
      <ModalContent className="px-4 py-4 overflow-y-auto">
        <ModalBody className="flex flex-col gap-8 overflow-y-auto">
          {/* Anomaly Creation Modal */}
          <AddModal
            isOpen={isModalOpen}
            setIsOpen={setIsModalOpen}
            threadResp={threadResp}
          />
          
          {/* Header Section */}
          <div className="flex items-center justify-between">
            <div>Anomaly</div>
            <Button
              color="primary"
              size="sm"
              onClick={() => setIsModalOpen(true)}
            >
              Add Anomaly
            </Button>
          </div>
          
          {/* Visualization Area */}
          <div className="h-[350px] border border-divider rounded-lg"></div>
          
          {/* Anomaly Type Display */}
          <div>
            <Code color="secondary">{threadResp?.anomaly}</Code>
          </div>
          
          {/* Analysis Tabs */}
          <Tabs
            size="sm"
            selectedKey={selectedTab}
            onSelectionChange={setSelectedTab}
          >
            <Tab key="contri" title="Top Contributors"></Tab>
            <Tab key="heat" title="Heat Map"></Tab>
          </Tabs>
          
          {/* Tab Content */}
          <div className="flex-1 overflow-y-auto">
            {selectedTab === "contri" ? <></> : <></>}
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default AnomalyDrawer;
